import { useMemo } from 'react'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { useAuthStateSnapshot } from '@/lib/authStatus'

type Tone = 'success' | 'warning' | 'error' | 'info'

interface StatusDescriptor {
  /** Short headline shown in the pill (e.g. "Connected"). */
  label: string
  /** Quiet trailing hint (e.g. "Auto-sync on"). */
  hint: string
  /** Full sentence kept for screen readers. */
  description: string
  tone: Tone
}

function indicatorForState(
  isOnline: boolean,
  isAuthenticated: boolean,
  tokenStatus: 'unknown' | 'ready' | 'expired',
): StatusDescriptor {
  if (isOnline && isAuthenticated && tokenStatus === 'expired') {
    return {
      label: 'Session expired',
      hint: 'Reconnecting',
      description: 'Re-authenticating to resume uploads and taxonomy refreshes.',
      tone: 'warning',
    }
  }

  if (isOnline && isAuthenticated) {
    return {
      label: 'Connected',
      hint: 'Auto-sync on',
      description: 'Uploads and taxonomy refreshes are ready.',
      tone: 'success',
    }
  }

  if (isOnline && !isAuthenticated) {
    return {
      label: 'Signed out',
      hint: 'Sign in to sync',
      description: 'Sign in to resume syncing and fetching data.',
      tone: 'warning',
    }
  }

  if (!isOnline && isAuthenticated) {
    return {
      label: 'Offline',
      hint: 'Saved locally',
      description: 'Captures are stored locally until the connection returns.',
      tone: 'info',
    }
  }

  return {
    label: 'Offline',
    hint: 'Capture only',
    description: 'Capture only mode — sign in once online to send drafts.',
    tone: 'error',
  }
}

interface ToneStyle {
  shell: string
  dot: string
  label: string
  hint: string
  ping: boolean
}

const TONE_STYLES: Record<Tone, ToneStyle> = {
  success: {
    shell: 'border-green-500/25 bg-green-500/[0.07]',
    dot: 'bg-green-400',
    label: 'text-green-300',
    hint: 'text-green-400/60',
    ping: true,
  },
  warning: {
    shell: 'border-amber-500/30 bg-amber-500/10',
    dot: 'bg-amber-400',
    label: 'text-amber-200',
    hint: 'text-amber-300/70',
    ping: false,
  },
  info: {
    shell: 'border-sky-500/30 bg-sky-500/10',
    dot: 'bg-sky-400',
    label: 'text-sky-200',
    hint: 'text-sky-300/70',
    ping: false,
  },
  error: {
    shell: 'border-red-500/30 bg-red-500/10',
    dot: 'bg-red-400',
    label: 'text-red-200',
    hint: 'text-red-300/70',
    ping: false,
  },
}

export function AuthStatusIndicator(): JSX.Element {
  const online = useOnlineStatus()
  const { isAuthenticated, tokenStatus } = useAuthStateSnapshot()

  const descriptor = useMemo(
    () => indicatorForState(online, isAuthenticated, tokenStatus),
    [online, isAuthenticated, tokenStatus],
  )
  const styles = TONE_STYLES[descriptor.tone]

  return (
    <div
      className={`status-pill flex h-12 w-full items-center gap-2.5 rounded-xl border px-4 transition-colors ${styles.shell}`}
      aria-live="polite"
    >
      <span className="relative flex h-2.5 w-2.5 shrink-0">
        {styles.ping && (
          <span
            className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${styles.dot}`}
            aria-hidden="true"
          />
        )}
        <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${styles.dot}`} aria-hidden="true" />
      </span>
      <span className={`status-pill-text text-sm font-semibold whitespace-nowrap ${styles.label}`}>
        {descriptor.label}
      </span>
      <span className={`status-pill-text ml-auto text-xs font-medium whitespace-nowrap ${styles.hint}`}>
        {descriptor.hint}
      </span>
      <span className="sr-only">{descriptor.description}</span>
    </div>
  )
}
